/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
// ============================================================
// HERO SECTION - Lightswind 3D Slider Integration (Responsive)
// ============================================================

import { useMemo } from "react";
import ThreeDSlider from "../../../components/lightswind/3d-slider.jsx";
import { publicStoreText } from "../../theme1/utils/theme1Helpers";

export default function HeroSection({
  storeData = {},
  displayBanners = [],
  theme,
}) {
  const safeTheme = theme || { primary: "#EC4899", accent: "#FCD34D" };
  const safeStore = storeData || {};
  const slogan = publicStoreText(safeStore.storeSlogan, safeStore.sectorType, safeStore.SectorType);
  const storeName = safeStore.storeName || safeStore.ClientName || "Our Store";

  const activeBanners = useMemo(
    () => (Array.isArray(displayBanners) ? displayBanners : []).filter((b) => b.active),
    [displayBanners]
  );

  const sliderItems = useMemo(() => {
    const base = activeBanners.map((banner, index) => ({
      title: banner.name || safeStore.storeName || "العرض",
      num: (banner.badge || banner.price || `${index + 1}`).toString().padStart(2, "0"),
      imageUrl: banner.imageUrl || "https://placehold.co/1200x800",
      data: banner,
    }));
    if (base.length === 0) return [];
    const filled = [...base];
    let i = 0;
    while (filled.length < 5) {
      const src = base[i % base.length];
      filled.push({ ...src, num: `${filled.length + 1}`.padStart(2, "0") });
      i++;
    }
    return filled;
  }, [activeBanners, safeStore]);

  return (
    <section className="relative overflow-hidden border-b border-white/10 bg-[#0f0f0f]">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/25 via-transparent to-pink-900/25 pointer-events-none" />
      <div className="absolute -left-32 top-10 w-64 h-64 sm:w-80 sm:h-80 bg-purple-500/15 blur-[120px] rounded-full" />
      <div className="absolute -right-24 -bottom-10 w-72 h-72 sm:w-96 sm:h-96 bg-pink-500/15 blur-[140px] rounded-full" />

      <div className="relative max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-14">
        <div className={`grid gap-5 sm:gap-10 lg:gap-14 items-center ${sliderItems.length > 0 ? "md:grid-cols-2" : ""}`}>
          <div className={`space-y-3 sm:space-y-6 text-center md:text-right ${sliderItems.length > 0 ? "order-2 md:order-1" : ""}`}>
            <div className="space-y-2 sm:space-y-3">
              <h2 className="text-xl sm:text-4xl md:text-5xl font-black text-white leading-tight">
                أهلاً بكم في{" "}
                <span
                  className="inline-block bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"
                  dir="ltr"
                >
                  {storeName}
                </span>
              </h2>

              <p className="text-xs sm:text-base text-white/70">
                {slogan || "تسوق إلكتروني مميز – اختيارات منتقاة وتجربة سلسة على كل الأجهزة."}
              </p>
            </div>
          </div>

          {sliderItems.length > 0 && (
            <div className="relative order-1 md:order-2 flex justify-center items-center py-2 sm:py-10 w-full">
              <div className="w-full max-w-[760px] mx-auto relative">
                <div className="absolute inset-4 sm:inset-6 rounded-[30px] bg-gradient-to-r from-purple-500/25 via-pink-500/18 to-amber-400/18 blur-3xl -z-10" />
                <ThreeDSlider
                  items={sliderItems}
                  containerStyle={{
                    height: "min(48vh, 380px)",
                    minHeight: "200px",
                    margin: "0 auto",
                    background: "transparent",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
